import Loader from "@components/Loader";
import ExistingExtraValues from "@components/dashboard/extraValues/ExistingExtraValues";
import ExtraValueForm from "@components/dashboard/extraValues/ExtraValueForm";
import ExtraValuesAiPanel from "@components/dashboard/extraValues/ExtraValuesAiPanel";
import ExtraValuesEap from "@components/dashboard/extraValues/ExtraValuesEap";
import ExtraValuesMonthDetailModal from "@components/dashboard/extraValues/ExtraValuesMonthDetailModal";
import ExtraValuesMonthlyChart from "@components/dashboard/extraValues/ExtraValuesMonthlyChart";
import ExtraValuesMonthlyTable from "@components/dashboard/extraValues/ExtraValuesMonthlyTable";
import ExtraValuesSummary, {
  formatExtraValueCents,
} from "@components/dashboard/extraValues/ExtraValuesSummary";
import ExtraValuesWorkspace from "@components/dashboard/extraValues/ExtraValuesWorkspace";
import {
  aggregateExtraValuesByMonth,
  extraValueMonths,
  toMonthLabel,
} from "@components/dashboard/extraValues/extraValuesMonthly";
import {
  deriveExtraValuesSummary,
  documentsFromState,
  extraValuesDocumentsReducer,
} from "@components/dashboard/extraValues/extraValuesState";
import AiPanel from "@components/dashboard/disbursement/ai/AiPanel";
import useAiQuota, {
  quotaNote,
} from "@components/dashboard/disbursement/ai/useAiQuota";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { isPresentationProject } from "@utils/presentationMode";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

const ExtraValuesScreen = ({ workId }) => {
  const actions = useProjectActions();
  const { activeProject, cubBased } = useProjectContext();
  const readOnly = isPresentationProject(activeProject);
  const [documentsState, dispatchDocuments] = useReducer(
    extraValuesDocumentsReducer,
    new Map(),
  );
  const documents = useMemo(
    () => documentsFromState(documentsState),
    [documentsState],
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [monthlyTableVisible, setMonthlyTableVisible] = useState(true);
  const [monthFrom, setMonthFrom] = useState("");
  const [monthTo, setMonthTo] = useState("");
  const [monthDetail, setMonthDetail] = useState(null);
  const [highlightedItem, setHighlightedItem] = useState(null);
  const requestGeneration = useRef(0);
  const requestController = useRef(null);
  const aiAccess = useAiQuota(workId, !readOnly);

  const loadDocuments = useCallback(
    async ({ showLoading = false } = {}) => {
      requestGeneration.current += 1;
      const generation = requestGeneration.current;
      requestController.current?.abort();
      const controller = new AbortController();
      requestController.current = controller;
      if (showLoading) setLoading(true);
      setLoadError(null);
      try {
        const response = actions.getExtraValuesByWork
          ? await actions.getExtraValuesByWork({ signal: controller.signal })
          : await actions.getExtraValues(activeProject?.building_id);
        if (generation !== requestGeneration.current) return false;
        if (response?.ok === false || !Array.isArray(response?.response)) {
          setLoadError(
            response?.message ?? "Não foi possível carregar os lançamentos.",
          );
          return false;
        }
        dispatchDocuments({
          type: "replace_all",
          documents: response.response,
        });
        return true;
      } catch (error) {
        if (generation === requestGeneration.current) {
          setLoadError(
            error?.message ?? "Não foi possível carregar os lançamentos.",
          );
        }
        return false;
      } finally {
        if (generation === requestGeneration.current) setLoading(false);
      }
    },
    [workId, activeProject?.building_id],
  );

  useEffect(() => {
    loadDocuments({ showLoading: true });
    return () => {
      requestGeneration.current += 1;
      requestController.current?.abort();
    };
  }, [loadDocuments]);

  const summary = useMemo(
    () => deriveExtraValuesSummary(documents),
    [documents],
  );
  const monthly = useMemo(() => {
    const { months: allMonths } = extraValueMonths(documents);
    const from = monthFrom || allMonths[0];
    const to = monthTo || allMonths[allMonths.length - 1];
    return {
      ...aggregateExtraValuesByMonth(documents, from, to),
      allMonths,
      allMonthLabels: allMonths.map(toMonthLabel),
    };
  }, [documents, monthFrom, monthTo]);

  const indicators = useMemo(
    () =>
      new Map(
        [...summary.indicators].map(([wbs, indicator]) => [
          wbs,
          {
            ...indicator,
            label: formatExtraValueCents(indicator.balanceCents, {
              cubBased,
              indexerValue: activeProject?.indexer?.value,
            }),
          },
        ]),
      ),
    [summary.indicators, cubBased, activeProject?.indexer?.value],
  );

  const confirmedDocument = async (response) => {
    if (response?.code !== 200) return false;
    if (response.document)
      dispatchDocuments({ type: "upsert", document: response.document });
    else await loadDocuments();
    return true;
  };

  const createEntry = async (entry) => {
    const response = await actions.updateExtra(
      selectedTarget.wbs_code_id,
      { operation: "append", entry },
      selectedTarget.description ??
        selectedTarget.wbs_description ??
        selectedTarget.wbs_code_id,
    );
    const ok = await confirmedDocument(response);
    if (ok) {
      const wbs = selectedTarget.wbs_code_id;
      setTimeout(() => {
        setSelectedTarget(null);
        document.getElementById(`extra-values-eap-${wbs}`)?.focus?.();
      }, 0);
    }
    return { ok, ...response };
  };

  const goToItem = (wbsCodeId) => {
    setMonthDetail(null);
    setHighlightedItem(wbsCodeId);
    setTimeout(
      () =>
        document
          .getElementById(`created-item-${wbsCodeId}`)
          ?.scrollIntoView?.({ behavior: "auto", block: "center" }),
      0,
    );
    setTimeout(() => setHighlightedItem(null), 2500);
  };

  const summaryBlock = (
    <div className="extra-values-overview">
      <header className="extra-values-section-heading">
        <div>
          <h2>Visão geral financeira</h2>
          <p>Impacto consolidado dos lançamentos ativos nesta obra.</p>
        </div>
      </header>
      <ExtraValuesSummary
        summary={summary}
        loading={loading}
        loadError={loadError}
      />
    </div>
  );

  const createdBlock = (
    <ExistingExtraValues
      documents={documents}
      readOnly={readOnly}
      highlightedWbs={highlightedItem}
      onDocument={(document) => dispatchDocuments({ type: "upsert", document })}
      onMissingDocument={() => loadDocuments()}
      onOpenAi={() => setAiOpen(true)}
      quotaText={quotaNote(aiAccess.quota)}
    />
  );

  const monthlyBlock = (
    <div className="extra-values-monthly">
      <header className="extra-values-section-heading">
        <div>
          <h2>Lançamentos por mês</h2>
          <p>Distribuição dos estouros e economias por competência.</p>
        </div>
        {monthly.allMonths.length ? (
          <div className="extra-values-monthly__toolbar">
            <label className="extra-values-monthly__period">
              <span>De</span>
              <select
                aria-label="Mês inicial"
                value={monthFrom || monthly.allMonths[0]}
                onChange={(event) => setMonthFrom(event.target.value)}
              >
                {monthly.allMonths.map((month) => (
                  <option key={month} value={month}>
                    {toMonthLabel(month)}
                  </option>
                ))}
              </select>
            </label>
            <label className="extra-values-monthly__period">
              <span>Até</span>
              <select
                aria-label="Mês final"
                value={monthTo || monthly.allMonths.at(-1)}
                onChange={(event) => setMonthTo(event.target.value)}
              >
                {monthly.allMonths.map((month) => (
                  <option key={month} value={month}>
                    {toMonthLabel(month)}
                  </option>
                ))}
              </select>
            </label>
            <div
              className="extra-values-view-switch"
              role="group"
              aria-label="Visualização mensal"
            >
              <button
                type="button"
                aria-pressed={monthlyTableVisible}
                className={monthlyTableVisible ? "is-active" : ""}
                onClick={() => setMonthlyTableVisible(true)}
              >
                Tabela
              </button>
              <button
                type="button"
                aria-pressed={!monthlyTableVisible}
                className={!monthlyTableVisible ? "is-active" : ""}
                onClick={() => setMonthlyTableVisible(false)}
              >
                Gráfico
              </button>
            </div>
          </div>
        ) : null}
      </header>
      {loading ? <p role="status">Carregando resumo mensal…</p> : null}
      {!loading && loadError ? <p role="alert">{loadError}</p> : null}
      {!loading && !loadError && monthly.error ? (
        <div className="alert alert-error" role="alert">
          <h3>Lançamentos por mês indisponíveis</h3>
          <p>{monthly.error.message}</p>
        </div>
      ) : null}
      {!loading &&
      !loadError &&
      !monthly.error &&
      monthly.months.length === 0 ? (
        <p className="empty-state">Ainda não há competências para exibir.</p>
      ) : null}
      {!loading &&
      !loadError &&
      !monthly.error &&
      monthly.months.length > 0 &&
      monthlyTableVisible ? (
        <ExtraValuesMonthlyTable
          months={monthly.months}
          monthLabels={monthly.monthLabels}
          rows={monthly.rows}
          net={monthly.net}
          totals={monthly.totals}
          onSelectRow={goToItem}
        />
      ) : null}
      {!loading &&
      !loadError &&
      !monthly.error &&
      monthly.months.length > 0 &&
      !monthlyTableVisible ? (
        <ExtraValuesMonthlyChart
          months={monthly.months}
          monthLabels={monthly.monthLabels}
          expenses={monthly.expenses}
          savings={monthly.savings}
          onBarClick={setMonthDetail}
        />
      ) : null}
    </div>
  );

  return (
    <>
      {loading ? (
        <div className="margin-top-24">
          <Loader />
        </div>
      ) : null}
      <ExtraValuesWorkspace
        workId={workId}
        summary={summaryBlock}
        created={createdBlock}
        monthly={monthlyBlock}
        detail={
          <ExtraValuesEap
            indicators={indicators}
            readOnly={readOnly}
            onCreate={setSelectedTarget}
          />
        }
        panel={
          <>
            {selectedTarget && !readOnly ? (
              <AiPanel
                title="Novo estouro ou economia"
                className="extra-value-entry-panel"
                onClose={() => setSelectedTarget(null)}
              >
                <ExtraValueForm
                  target={selectedTarget}
                  onSubmit={createEntry}
                  onCancel={() => setSelectedTarget(null)}
                />
              </AiPanel>
            ) : null}
            {aiOpen && !readOnly ? (
              <ExtraValuesAiPanel
                workId={workId}
                onClose={() => setAiOpen(false)}
                onExecuted={async () => {
                  await loadDocuments();
                  setAiOpen(false);
                }}
              />
            ) : null}
          </>
        }
      />
      {monthDetail ? (
        <ExtraValuesMonthDetailModal
          monthLabel={toMonthLabel(monthDetail)}
          entries={monthly.entriesByMonth[monthDetail] || []}
          onSelectItem={goToItem}
          close={() => setMonthDetail(null)}
        />
      ) : null}
    </>
  );
};

const ExtraValues = () => {
  const { activeProject } = useProjectContext();
  const workId = activeProject?._id ?? null;
  if (!workId) return null;
  return <ExtraValuesScreen key={workId} workId={workId} />;
};

export default ExtraValues;
